import * as Yup from "yup";

type Translator = (key: string, opts?: any) => string;

export const MIN_CITY_STATE_LENGTH = 3;
export const MIN_STREET_LENGTH = 5;

// Obviously fake / placeholder values that must never pass for a real address.
const JUNK_VALUES = new Set<string>([
  "test",
  "tester",
  "testing",
  "xxx",
  "xxxx",
  "xxxxx",
  "asd",
  "asdf",
  "asdfgh",
  "qwe",
  "qwer",
  "qwerty",
  "aaa",
  "aaaa",
  "abc",
  "abcd",
  "abcde",
  "none",
  "na",
  "n/a",
  "nil",
  "null",
  "undefined",
  "sample",
  "example",
  "fake",
  "dummy",
  "unknown",
  "city",
  "state",
  "address",
  "street",
  "zzz",
]);

// Canadian postal code: A1A 1A1 (space optional, case-insensitive), excluding
// letters not used by Canada Post (D, F, I, O, Q, U, and W/Z in first position).
const CA_POSTAL_REGEX =
  /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\s?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

// Generic, intentionally loose postal/ZIP check for other countries.
const GENERIC_POSTAL_REGEX =
  /^(?=.{3,12}$)[A-Za-z0-9][A-Za-z0-9 -]*[A-Za-z0-9]$/;

const normalize = (value?: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

export const isNumbersOnly = (value?: string): boolean => {
  const v = String(value ?? "").trim();
  return v.length > 0 && /^[0-9\s]+$/.test(v);
};

export const isJunkValue = (value?: string): boolean =>
  JUNK_VALUES.has(normalize(value));

export const isCanada = (country?: string): boolean => {
  const n = normalize(country);
  return n === "canada" || n === "ca";
};

// Countries whose addresses include a state/province, stored both by full name
// (the value coming from the country dropdown) and ISO code, normalized.
const COUNTRIES_REQUIRING_STATE = new Set<string>([
  "united states",
  "united states of america",
  "usa",
  "us",
  "canada",
  "ca",
  "australia",
  "au",
  "india",
  "in",
  "brazil",
  "br",
  "mexico",
  "mx",
  "argentina",
  "ar",
  "china",
  "cn",
]);

export const requiresState = (country?: string): boolean =>
  COUNTRIES_REQUIRING_STATE.has(normalize(country));

const matchesCountryName = (value: string, parent: any): boolean => {
  const n = normalize(value);
  if (!n) return false;
  return [normalize(parent?.country), normalize(parent?.citizenship_country)]
    .filter(Boolean)
    .includes(n);
};

export const isValidPostalCode = (
  value?: string,
  country?: string
): boolean => {
  const v = String(value ?? "").trim();
  if (!v) return false;
  if (isCanada(country)) return CA_POSTAL_REGEX.test(v);
  return GENERIC_POSTAL_REGEX.test(v) && !/^\d{1,2}$/.test(v);
};

// Shared quality check for free-text address parts (city / state / street).
// When `required` is false an empty value is accepted, but any entered value
// still has to pass the quality rules.
function buildTextField(
  t: Translator,
  requiredKey: string,
  invalidKey: string,
  minLength: number,
  required = true
): Yup.StringSchema {
  const schema = required
    ? Yup.string().required(t(requiredKey))
    : Yup.string();

  return schema.test(
    "address-quality",
    t(invalidKey),
    function qualityCheck(value) {
      const v = (value || "").trim();
      if (!v) return !required;
      if (v.length < minLength) return false;
      if (isNumbersOnly(v)) return false;
      if (isJunkValue(v)) return false;
      if (matchesCountryName(v, this.parent)) return false;
      return true;
    }
  );
}

export function yupCity(t: Translator): Yup.StringSchema {
  return buildTextField(
    t,
    "address_policy.city_required",
    "address_policy.city_invalid",
    MIN_CITY_STATE_LENGTH
  );
}

export function yupState(t: Translator, required = true): Yup.StringSchema {
  return buildTextField(
    t,
    "address_policy.state_required",
    "address_policy.state_invalid",
    MIN_CITY_STATE_LENGTH,
    required
  );
}

export function yupStreet(t: Translator): Yup.StringSchema {
  return buildTextField(
    t,
    "address_policy.address_required",
    "address_policy.address_invalid",
    MIN_STREET_LENGTH
  );
}

export function yupPostalCode(t: Translator): Yup.StringSchema {
  return Yup.string()
    .required(t("address_policy.postal_required"))
    .test("postal", "", function postalCheck(value) {
      const country = (this.parent as any)?.country as string | undefined;
      if (isValidPostalCode(value || "", country)) return true;
      return this.createError({
        message: isCanada(country)
          ? t("address_policy.postal_invalid_ca")
          : t("address_policy.postal_invalid"),
      });
    });
}
